import { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Route as RouteIcon,
  MapPin,
  Navigation,
  Clock,
  ChevronRight,
  CheckCircle,
  AlertTriangle,
  Home,
  Target,
  Zap,
  Download,
  X,
  Mic,
  History as HistoryIcon,
  Radio,
  Globe,
  Building2,
  Compass,
  Share2,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import { db } from '../utils/firebase/client';
import { collection, query, orderBy, onSnapshot, addDoc, getDocs, where } from 'firebase/firestore';
import { getCompanyId } from '../utils/tenantContext';
import { resolveWellCoordinates } from '../utils/location/coordinates';
import {
  routeOptimizationEngine,
  OptimizedRoute,
  VehicleConstraints,
  OptimizationOptions,
  WellLocation
} from '../utils/routing/RouteOptimizationEngine';
import SmartRouteOptimizer from './SmartRouteOptimizer';
import IndustryWellExplorer from './navigation/IndustryWellExplorer';
import { OCCCompanyWell } from '../utils/intelligence/occCompanySearch';
import { routeSharingService, SharedRoute } from '../utils/routing/routeSharingService';

interface RoutesMobileProps {
  wells: any[];
  onSelectWell?: (well: any) => void;
  onNavigate?: (screen: string) => void;
  onOpenXAssist?: () => void;
  isDarkMode?: boolean;
  onToggleTheme?: () => void;
  currentUser?: { id: string; name: string };
  onClose?: () => void;
  onStartNavigation?: (route: any) => void;
}

export default function RoutesMobile({
  wells,
  onSelectWell: _onSelectWell,
  onNavigate: _onNavigate = () => { },
  onOpenXAssist: _onOpenXAssist = () => { },
  isDarkMode = false,
  onToggleTheme: _onToggleTheme = () => { },
  currentUser,
  onStartNavigation = () => { },
}: RoutesMobileProps) {
  const [activeSubTab, setActiveSubTab] = useState('planner');
  const [selectedWells, setSelectedWells] = useState<string[]>([]);
  const [visitedWells, _setVisitedWells] = useState<string[]>([]); // Track completed wells
  const [startLocation, setStartLocation] = useState('');
  const [startLocationCoords, setStartLocationCoords] = useState<{ lat: number, lng: number } | null>(null);
  const [optimizing, setOptimizing] = useState(false);
  const [showSolverAnimation, setShowSolverAnimation] = useState(false);
  const [optimizedRoute, setOptimizedRoute] = useState<OptimizedRoute | null>(null);
  const [showVoiceNavigation, setShowVoiceNavigation] = useState(false);
  const [showIndustryExplorer, setShowIndustryExplorer] = useState(false);
  const [lastOptimizationMetrics, setLastOptimizationMetrics] = useState<{
    algorithm: string;
    improvementPercent: number;
    timeMs: number;
    validationIssues: number;
  } | null>(null);
  const [preferences, setPreferences] = useState({
    prioritizeOverdue: true,
    minimizeDistance: true,
    optimizeForTime: false,
  });

  // Real-time data
  const [routes, setRoutes] = useState<any[]>([]);
  const [_loading, setLoading] = useState(true);

  // Feature 5: Route Sharing State
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [teamMembers, setTeamMembers] = useState<{ id: string; name: string }[]>([]);
  const [selectedShareTargets, setSelectedShareTargets] = useState<string[]>([]);
  const [isSharing, setIsSharing] = useState(false);
  const [sharedRoutes, setSharedRoutes] = useState<SharedRoute[]>([]);

  const companyId = getCompanyId();


  // Load routes in real-time
  useEffect(() => {
    if (!companyId) return;

    const routesRef = collection(db, `companies/${companyId}/routes`);
    const q = query(routesRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const routesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRoutes(routesData);
      setLoading(false);
    }, (error) => {
      console.error('Error loading routes:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [companyId]);

  // Feature 5: Load team members for sharing
  useEffect(() => {
    const loadTeamMembers = async () => {
      if (!companyId) return;

      try {
        const usersRef = collection(db, `companies/${companyId}/users`);
        const usersSnap = await getDocs(usersRef);
        const members = usersSnap.docs
          .map(doc => ({
            id: doc.id,
            name: doc.data().name || doc.data().email || 'Unknown'
          }))
          .filter(m => m.id !== currentUser?.id); // Exclude self
        setTeamMembers(members);
      } catch (error) {
        console.error('Failed to load team members:', error);
      }
    };

    loadTeamMembers();
  }, [companyId, currentUser?.id]);

  // Feature 5: Load routes shared with me
  useEffect(() => {
    const loadSharedRoutes = async () => {
      if (!currentUser?.id || !companyId) return;

      try {
        const shared = await routeSharingService.getSharedRoutesForUser(currentUser.id, companyId);
        setSharedRoutes(shared);
      } catch (error) {
        console.error('Failed to load shared routes:', error);
      }
    };

    loadSharedRoutes();
  }, [currentUser?.id, companyId]);

  // Calculate well status and categorize
  const categorizedWells = useMemo(() => {
    const critical: any[] = [];
    const overdue: any[] = [];
    const dueSoon: any[] = [];
    const upToDate: any[] = [];

    wells.forEach(well => {
      if (!well.lastEntry) {
        critical.push(well);
        return;
      }

      const daysSince = (Date.now() - new Date(well.lastEntry).getTime()) / (1000 * 60 * 60 * 24);

      if (daysSince > 14) {
        critical.push(well);
      } else if (daysSince > 7) {
        overdue.push(well);
      } else if (daysSince > 4) {
        dueSoon.push(well);
      } else {
        upToDate.push(well);
      }
    });

    return { critical, overdue, dueSoon, upToDate };
  }, [wells]);

  const getWellStatus = (well: any) => {
    if (!well.lastEntry) {
      return {
        label: 'Never',
        // Dark mode: bold vibrant red, Light mode: subtle red
        colorLight: 'bg-red-100 text-red-800',
        colorDark: 'bg-red-600 text-white',
        days: 999,
        priority: 4
      };
    }

    const daysSince = Math.floor((Date.now() - new Date(well.lastEntry).getTime()) / (1000 * 60 * 60 * 24));

    if (daysSince > 14) {
      return {
        label: `${daysSince}d ago`,
        colorLight: 'bg-red-100 text-red-800',
        colorDark: 'bg-red-600 text-white',
        days: daysSince,
        priority: 3
      };
    } else if (daysSince > 7) {
      return {
        label: `${daysSince}d ago`,
        colorLight: 'bg-orange-100 text-orange-800',
        colorDark: 'bg-orange-500 text-white',
        days: daysSince,
        priority: 2
      };
    } else if (daysSince > 4) {
      return {
        label: `${daysSince}d ago`,
        colorLight: 'bg-yellow-100 text-yellow-800',
        colorDark: 'bg-amber-500 text-white',
        days: daysSince,
        priority: 1
      };
    } else {
      return {
        label: `${daysSince}d ago`,
        colorLight: 'bg-green-100 text-green-800',
        colorDark: 'bg-emerald-600 text-white',
        days: daysSince,
        priority: 0
      };
    }
  };

  const handleWellToggle = (wellId: string) => {
    setSelectedWells(prev =>
      prev.includes(wellId)
        ? prev.filter(id => id !== wellId)
        : [...prev, wellId]
    );
  };

  const selectAllOverdue = () => {
    const overdueIds = [...categorizedWells.critical, ...categorizedWells.overdue]
      .map(w => w.id);
    setSelectedWells(overdueIds);
    toast.success(`Selected ${overdueIds.length} wells needing attention`);
  };

  const selectAll = () => {
    setSelectedWells(wells.map(w => w.id));
    toast.success(`Selected all ${wells.length} wells`);
  };

  const clearSelection = () => {
    setSelectedWells([]);
  };

  const getVehicleConstraints = (): VehicleConstraints => {
    // Standard fleet vehicle configuration for OilWellX pumpers
    // These values can be customized per company/vehicle in future
    return {
      fuelCapacity: 20, // gallons (standard pickup truck)
      fuelConsumption: 15, // mpg (average for field trucks)
      maxDailyHours: 10, // hours (regulatory + practical limit)
      maxDailyDistance: 150, // miles (safe daily limit for field work)
      requiredBreakEvery: 4, // hours (safety requirement)
      maxPayloadLbs: 500, // pounds (tools + parts capacity)
      costPerMile: 0.65 // IRS standard mileage rate
    };
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    toast.info('Getting your location...');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setStartLocationCoords({ lat: latitude, lng: longitude });
        setStartLocation(`Current Location (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`);
        toast.success('Location updated');
      },
      (error) => {
        console.error('Error getting location:', error);
        toast.error('Failed to get location. Please enable GPS.');
      }
    );
  };

  const optimizeRoute = async () => {
    if (selectedWells.length < 1) {
      toast.error('Select at least 1 well to optimize');
      return;
    }

    // Single well direct navigation
    if (selectedWells.length === 1) {
      const well = wells.find(w => w.id === selectedWells[0]);
      if (well) {
        const coords = resolveWellCoordinates(well);
        if (!coords) {
          toast.error('Missing GPS coordinates for selected well');
          return;
        }

        // Create a direct route object
        const directRoute = {
          id: `direct_${well.id}_${Date.now()}`,
          name: `Direct to ${well.name}`,
          wells: [{
            wellId: well.id,
            wellName: well.name,
            order: 1,
            coordinates: coords,
            estimatedTime: 0.5
          }],
          totalDistance: 0,
          totalDuration: 0,
          optimized: false,
          status: 'active',
          companyId,
          createdAt: new Date(),
          createdBy: currentUser?.id
        };

        onStartNavigation(directRoute);
        return;
      }
    }

    if (selectedWells.length < 2 && !startLocationCoords) {
      toast.error('Select at least 2 wells, or 1 well with a starting location');
      return;
    }

    setOptimizing(true);
    setShowSolverAnimation(true);
    const startTime = performance.now();

    try {
      const selectedWellsData = wells.filter(w => selectedWells.includes(w.id));

      // Prepare wells for optimization engine
      const missingCoords: string[] = [];
      const wellLocations: WellLocation[] = selectedWellsData.map((w) => {
        const coords = resolveWellCoordinates(w);
        if (!coords) {
          missingCoords.push(w.name || w.id);
          return null;
        }

        // Map well priority based on days since last visit
        let priority: 'low' | 'medium' | 'high' | 'emergency' = 'medium';
        if (w.lastEntry) {
          const daysSince = (Date.now() - new Date(w.lastEntry).getTime()) / (1000 * 60 * 60 * 24);
          if (daysSince > 14) priority = 'emergency';
          else if (daysSince > 7) priority = 'high';
          else if (daysSince > 4) priority = 'medium';
          else priority = 'low';
        } else {
          priority = 'emergency'; // Never visited
        }

        return {
          id: w.id,
          name: w.name || w.id,
          latitude: coords.lat,
          longitude: coords.lng,
          priority,
          estimatedServiceTime: 15, // 15 minutes default service time
          lastVisit: w.lastEntry ? new Date(w.lastEntry) : undefined,
          status: w.status || 'pending'
        };
      }).filter(w => w !== null) as WellLocation[];

      if (missingCoords.length > 0) {
        throw new Error(`Missing GPS coordinates for: ${missingCoords.join(', ')}`);
      }

      // Configure optimization options
      const optimizationOptions: OptimizationOptions = {
        algorithm: '2opt', // Use 2-OPT for 15-25% improvement
        startLocation: startLocationCoords || undefined,
        vehicleConstraints: getVehicleConstraints(),
        prioritizeByDaysOverdue: preferences.prioritizeOverdue,
        maxIterations: 100 // Max 2-OPT iterations
      };

      // Run optimization engine
      const optimizedRoute = await routeOptimizationEngine.optimize(
        wellLocations,
        optimizationOptions
      );

      const endTime = performance.now();
      const optimizationTimeMs = Math.round(endTime - startTime);

      // Store metrics for UI display
      setLastOptimizationMetrics({
        algorithm: optimizedRoute.algorithm,
        improvementPercent: optimizedRoute.improvementFromNN || 0,
        timeMs: optimizationTimeMs,
        validationIssues: optimizedRoute.validation?.issues.length || 0
      });

      // Show validation warnings if any
      if (optimizedRoute.validation?.warnings && optimizedRoute.validation.warnings.length > 0) {
        optimizedRoute.validation.warnings.forEach(warning => {
          toast.warning(warning, { duration: 5000 });
        });
      }

      // Show validation errors if route is invalid
      if (optimizedRoute.validation?.issues && optimizedRoute.validation.issues.length > 0) {
        optimizedRoute.validation.issues.forEach(issue => {
          toast.error(issue, { duration: 7000 });
        });
      }

      // Prepare route data for Firestore with backward compatibility
      const routeData = {
        name: `Route ${new Date().toLocaleDateString()}`,
        wells: optimizedRoute.waypoints.map((well, index) => ({
          wellId: well.id,
          wellName: well.name,
          order: index + 1,
          coordinates: { lat: well.latitude, lng: well.longitude },
          estimatedTime: (well.estimatedServiceTime || 15) / 60 // Convert to hours
        })),
        totalDistance: optimizedRoute.totalDistance,
        estimatedDuration: optimizedRoute.totalDuration / 60, // Convert to hours
        optimized: true,
        algorithm: optimizedRoute.algorithm,
        efficiency: optimizedRoute.efficiency,
        cost: optimizedRoute.cost,
        validation: optimizedRoute.validation,
        improvementPercent: optimizedRoute.improvementFromNN,
        status: 'active',
        companyId,
        createdAt: new Date(),
        createdBy: currentUser?.id,
      };

      // Save to Firestore
      if (navigator.onLine) {
        try {
          const routesRef = collection(db, `companies/${companyId}/routes`);
          const docRef = await addDoc(routesRef, routeData);

          // Set optimized route with Firestore ID
          const finalRoute: OptimizedRoute = {
            ...optimizedRoute,
            id: docRef.id
          };
          setOptimizedRoute(finalRoute);
        } catch (e) {
          console.error('Error saving route:', e);
          setOptimizedRoute({ ...optimizedRoute, id: 'local_' + Date.now() });
          toast.warning('Route saved locally (offline)');
        }
      } else {
        setOptimizedRoute({ ...optimizedRoute, id: 'local_' + Date.now() });
        toast.info('Route generated offline');
      }



      // Wait for animation to finish if it hasn't already (minimum 2.5s total)
      // The SmartRouteOptimizer takes about 5.5s.
      // We don't hide the animation here; we let the OnComplete callback handle it.

      // We DO NOT set ActiveSubTab here yet. We wait for animation.

      // Show success toast with improvement metrics
      const improvementMsg = optimizedRoute.improvementFromNN
        ? ` (${optimizedRoute.improvementFromNN}% better than nearest neighbor)`
        : '';
      toast.success(
        `Route optimized in ${optimizationTimeMs}ms${improvementMsg}`,
        { duration: 4000 }
      );

    } catch (error: any) {
      console.error('Error optimizing route:', error);
      toast.error(`Failed to optimize route: ${error.message}`);
      setOptimizing(false);
      setShowSolverAnimation(false);
    }
    // Finally block removed - we handle state change in onSolverComplete
  };

  const onSolverComplete = () => {
    setShowSolverAnimation(false);
    setOptimizing(false);
    setActiveSubTab('active');
  };

  // Local optimization functions removed - now handled by RouteOptimizationEngine
  // See src/utils/routing/RouteOptimizationEngine.ts for implementation

  const startNavigation = () => {
    if (!optimizedRoute) {
      toast.error('No active route to navigate');
      return;
    }

    // Check waypoints (not wells - using new engine structure)
    if (!optimizedRoute.waypoints || optimizedRoute.waypoints.length === 0) {
      toast.error('Route has no waypoints');
      return;
    }

    // Validate all waypoints have coordinates
    const missingCoords = optimizedRoute.waypoints.find(
      (waypoint) => !waypoint.latitude || !waypoint.longitude
    );
    if (missingCoords) {
      toast.error(`Missing coordinates for ${missingCoords.name || 'a waypoint'}`);
      return;
    }

    // Check validation status
    if (optimizedRoute.validation && !optimizedRoute.validation.isValid) {
      const issues = optimizedRoute.validation.issues.join('\n');
      toast.error(
        `Route has validation issues:\n${issues}\n\nPlease re-optimize with fewer wells or different constraints.`,
        { duration: 8000 }
      );
      return;
    }

    // Show detailed route summary
    const costSummary = optimizedRoute.cost
      ? `\nCost: $${optimizedRoute.cost.total.toFixed(2)} (Fuel: $${optimizedRoute.cost.fuel.toFixed(2)}, Labor: $${optimizedRoute.cost.labor})`
      : '';

    toast.success(
      `Starting navigation: ${optimizedRoute.totalDistance.toFixed(1)} miles, ${Math.round(optimizedRoute.totalDuration / 60)} hours${costSummary}`,
      { duration: 5000 }
    );

    onStartNavigation?.(optimizedRoute);
  };

  const exportRoute = () => {
    if (!optimizedRoute) return;

    const routeText = optimizedRoute.waypoints.map((well: WellLocation, index: number) =>
      `${index + 1}. ${well.name} - ${well.latitude}, ${well.longitude}`
    ).join('\n');

    const exportData = `Optimized Route - ${new Date().toLocaleDateString()}

Total Wells: ${optimizedRoute.waypoints.length}
Distance: ${optimizedRoute.totalDistance.toFixed(1)} miles
Estimated Time: ${optimizedRoute.totalDuration.toFixed(1)} hours

Route:
${routeText}

Generated by OilWellX`;

    const blob = new Blob([exportData], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `route-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success('Route exported successfully');
  };

  // Feature 5: Share route with team members
  const handleShareRoute = async () => {
    if (!optimizedRoute || !currentUser?.id || !companyId) {
      toast.error('Unable to share route');
      return;
    }

    if (selectedShareTargets.length === 0) {
      toast.error('Select at least one team member');
      return;
    }

    setIsSharing(true);

    try {
      // Convert optimized route to sharable format
      const routeForSharing = {
        name: `Route ${new Date().toLocaleDateString()}`,
        stops: optimizedRoute.waypoints.map((w, index) => ({
          id: w.id,
          name: w.name,
          latitude: w.latitude,
          longitude: w.longitude,
          order: index + 1
        })),
        totalDistance: optimizedRoute.totalDistance,
        estimatedDuration: optimizedRoute.totalDuration
      };

      const routeId = await routeSharingService.shareRoute(
        routeForSharing,
        selectedShareTargets,
        companyId,
        currentUser.id
      );

      toast.success(`Route shared with ${selectedShareTargets.length} team member(s)!`);
      setShowShareDialog(false);
      setSelectedShareTargets([]);
    } catch (error) {
      console.error('Failed to share route:', error);
      toast.error('Failed to share route');
    } finally {
      setIsSharing(false);
    }
  };

  // Feature 5: Accept a shared route
  const handleAcceptSharedRoute = async (sharedRoute: SharedRoute) => {
    if (!currentUser?.id || !companyId) return;

    try {
      await routeSharingService.acceptSharedRoute(sharedRoute.id, currentUser.id, companyId);

      // Convert to OptimizedRoute format
      const convertedRoute: OptimizedRoute = {
        id: sharedRoute.id,
        waypoints: sharedRoute.stops.map(s => ({
          id: s.id,
          name: s.name,
          latitude: s.latitude,
          longitude: s.longitude
        })) as any,
        totalDistance: sharedRoute.totalDistance || 0,
        totalDuration: sharedRoute.estimatedDuration || 0,
        algorithm: 'shared',
        efficiency: 0
      };

      setOptimizedRoute(convertedRoute);
      setActiveSubTab('active');
      toast.success(`Loaded ${sharedRoute.name} from ${sharedRoute.sharedBy}`);

      // Remove from shared list
      setSharedRoutes(prev => prev.filter(r => r.id !== sharedRoute.id));
    } catch (error) {
      console.error('Failed to accept shared route:', error);
      toast.error('Failed to load shared route');
    }
  };

  const loadHistoryRoute = (route: any) => {
    // Convert Firestore route to OptimizedRoute format
    const loadedRoute: OptimizedRoute = {
      id: route.id,
      waypoints: route.wells.map((w: any) => ({
        id: w.wellId,
        name: w.wellName,
        latitude: w.coordinates.lat,
        longitude: w.coordinates.lng,
        estimatedServiceTime: w.estimatedTime * 60,
        status: 'pending'
      })),
      totalDistance: route.totalDistance,
      totalDuration: route.estimatedDuration * 60,
      algorithm: route.algorithm || 'unknown',
      efficiency: route.efficiency,
      cost: route.cost,
      validation: route.validation,
      improvementFromNN: route.improvementPercent
    };

    setOptimizedRoute(loadedRoute);
    setActiveSubTab('active');
    toast.success(`Loaded route: ${route.name}`);
  };

  return (
    <div className="px-4 py-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <h1 className={`text-2xl mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Route Optimization
        </h1>
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Plan and optimize your well visit routes
        </p>
      </motion.div>

      {/* Main Tab Switch */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`
          flex gap-2 mb-6 p-1.5 rounded-2xl
          ${isDarkMode ? 'bg-gradient-to-br from-slate-800 to-slate-900' : 'bg-gray-100'}
        `}
      >
        {[
          { id: 'planner', label: 'Planner', icon: RouteIcon },
          { id: 'active', label: 'Active Route', icon: Navigation },
          { id: 'history', label: 'History', icon: HistoryIcon },
        ].map((tab) => (
          <motion.button
            key={tab.id}
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveSubTab(tab.id)}
            className={`
              flex-1 flex items-center justify-center gap-2 py-3 rounded-xl transition-all
              ${activeSubTab === tab.id
                ? 'bg-gradient-to-br from-cyan-600 to-teal-700 text-white shadow-lg'
                : (isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-900')
              }
            `}
          >
            <tab.icon className="w-4 h-4" />
            <span className="text-sm">{tab.label}</span>
          </motion.button>
        ))}
      </motion.div>

      {/* Voice Navigation Button - Always Visible */}
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => {
          setShowVoiceNavigation(true);
          if (_onOpenXAssist) _onOpenXAssist();
        }}
        className="w-full mb-6 relative overflow-hidden"
      >
        <div className={`
          relative p-1 rounded-2xl
          bg-gradient-to-r from-purple-600 to-pink-600
        `}>
          <div className={`
            ${isDarkMode ? 'bg-slate-900/80' : 'bg-white/90'}
            backdrop-blur rounded-[0.9rem] p-5 flex items-center gap-4
          `}>
            <motion.div
              animate={showVoiceNavigation ? {
                scale: [1, 1.2, 1],
                rotate: [0, 180, 360]
              } : {}}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center shadow-xl"
            >
              <Mic className="w-6 h-6 text-white" />
            </motion.div>
            <div className="flex-1 text-left">
              <div className={`text-base mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Voice Navigation
              </div>
              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {showVoiceNavigation ? 'Listening...' : 'Say "directions to well [name]"'}
              </div>
            </div>

            {/* Waveform Animation */}
            {showVoiceNavigation && (
              <div className="flex items-center gap-1 h-8">
                {[1, 2, 3].map((i) => (
                  <motion.div
                    key={i}
                    className="w-1.5 rounded-full bg-gradient-to-t from-purple-600 to-pink-600"
                    animate={{ height: [10, 24, 10] }}
                    transition={{
                      duration: 0.6,
                      repeat: Infinity,
                      delay: i * 0.1,
                      ease: "easeInOut"
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </motion.button>

      {/* 🌐 Industry Well Explorer - OCC Statewide Search */}
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setShowIndustryExplorer(true)}
        className="w-full mb-6 relative overflow-hidden"
      >
        <div className={`
          relative p-1 rounded-2xl
          bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-500
        `}>
          <div className={`
            ${isDarkMode ? 'bg-slate-900/80' : 'bg-white/90'}
            backdrop-blur rounded-[0.9rem] p-5 flex items-center gap-4
          `}>
            <motion.div
              animate={{
                rotate: [0, 360]
              }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="w-12 h-12 bg-gradient-to-br from-blue-600 via-cyan-500 to-teal-500 rounded-2xl flex items-center justify-center shadow-xl"
            >
              <Globe className="w-6 h-6 text-white" />
            </motion.div>
            <div className="flex-1 text-left">
              <div className="flex items-center gap-2">
                <span className={`text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Industry Well Explorer
                </span>
                <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white uppercase tracking-wider">
                  Pro
                </span>
              </div>
              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Search 50,000+ Oklahoma wells by company
              </div>
            </div>

            {/* Compass Animation */}
            <motion.div
              animate={{ rotate: [0, 15, -15, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
              <Compass className={`w-6 h-6 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
            </motion.div>
          </div>
        </div>
      </motion.button>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeSubTab === 'planner' && (
          <motion.div
            key="planner"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            {/* Critical Stats */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 }}
                className="p-1 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl"
              >
                <div className={`
                  ${isDarkMode ? 'bg-gradient-to-br from-red-600 to-rose-700' : 'bg-white/90'}
                  backdrop-blur rounded-[0.9rem] p-4
                `}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs ${isDarkMode ? 'text-white/80' : 'text-gray-600'}`}>Critical</span>
                    <AlertTriangle className={`w-4 h-4 ${isDarkMode ? 'text-white' : 'text-red-500'}`} />
                  </div>
                  <div className={`text-3xl ${isDarkMode ? 'text-white font-bold' : 'bg-gradient-to-br from-red-500 to-rose-600 bg-clip-text text-transparent'}`}>
                    {categorizedWells.critical.length}
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.15 }}
                className="p-1 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl"
              >
                <div className={`
                  ${isDarkMode ? 'bg-gradient-to-br from-orange-500 to-amber-600' : 'bg-white/90'}
                  backdrop-blur rounded-[0.9rem] p-4
                `}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs ${isDarkMode ? 'text-white/80' : 'text-gray-600'}`}>Overdue</span>
                    <Clock className={`w-4 h-4 ${isDarkMode ? 'text-white' : 'text-orange-500'}`} />
                  </div>
                  <div className={`text-3xl ${isDarkMode ? 'text-white font-bold' : 'bg-gradient-to-br from-orange-500 to-amber-600 bg-clip-text text-transparent'}`}>
                    {categorizedWells.overdue.length}
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Starting Point */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className={`
                p-1 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl mb-6
              `}
            >
              <div className={`
                ${isDarkMode ? 'bg-gradient-to-br from-blue-600 to-cyan-700' : 'bg-white/90'}
                backdrop-blur rounded-[0.9rem] p-5
              `}>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-white/20' : 'bg-gradient-to-br from-blue-500 to-cyan-600'}`}>
                    <Home className="w-5 h-5 text-white" />
                  </div>
                  <div className={`text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Starting Point
                  </div>
                </div>
                <div className={`
                  flex items-center rounded-xl border transition-all focus-within:ring-2 focus-within:ring-cyan-500
                  ${isDarkMode
                    ? 'bg-white/10 border-white/20'
                    : 'bg-gray-100 border-gray-200'
                  }
                `}>
                  <input
                    type="text"
                    value={startLocation}
                    onChange={(e) => {
                      setStartLocation(e.target.value);
                      setStartLocationCoords(null); // Clear coords if user types manually
                    }}
                    placeholder="Enter starting location or use GPS"
                    className={`
                      flex-1 px-4 py-3 text-sm bg-transparent border-none focus:ring-0 focus:outline-none
                      ${isDarkMode ? 'text-white placeholder-white/50' : 'text-gray-900 placeholder-gray-500'}
                    `}
                  />
                  <div className="pr-2">
                    <button
                      onClick={handleUseMyLocation}
                      className={`
                        p-2 rounded-lg flex items-center justify-center transition-colors
                        ${isDarkMode
                          ? 'text-white hover:bg-white/20'
                          : 'text-cyan-600 hover:bg-gray-200'
                        }
                      `}
                      title="Use My Location"
                    >
                      <MapPin className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <p className={`text-xs mt-2 ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>
                  This will be used as the route's starting point
                </p>
              </div>
            </motion.div>

            {/* Route Preferences */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className={`
                p-1 bg-gradient-to-br from-purple-500 to-violet-600 rounded-2xl mb-6
              `}
            >
              <div className={`
                ${isDarkMode ? 'bg-gradient-to-br from-purple-600 to-violet-700' : 'bg-white/90'}
                backdrop-blur rounded-[0.9rem] p-5
              `}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-white/20' : 'bg-gradient-to-br from-purple-500 to-violet-600'}`}>
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <div className={`text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Route Preferences
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    { key: 'prioritizeOverdue', label: 'Prioritize overdue wells', icon: AlertTriangle, color: 'from-orange-500 to-red-600' },
                    { key: 'minimizeDistance', label: 'Minimize total distance', icon: RouteIcon, color: 'from-blue-500 to-cyan-600' },
                    { key: 'optimizeForTime', label: 'Optimize for fastest time', icon: Zap, color: 'from-yellow-500 to-amber-600' },
                  ].map((pref) => (
                    <motion.button
                      key={pref.key}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setPreferences({ ...preferences, [pref.key]: !preferences[pref.key as keyof typeof preferences] })}
                      className={`
                        w-full flex items-center gap-3 p-3 rounded-xl transition-all
                        ${preferences[pref.key as keyof typeof preferences]
                          ? `bg-gradient-to-r ${pref.color}`
                          : (isDarkMode ? 'bg-slate-800/50 hover:bg-slate-800' : 'bg-gray-100 hover:bg-gray-200')
                        }
                      `}
                    >
                      <div className={`
                        w-6 h-6 rounded-lg flex items-center justify-center
                        ${preferences[pref.key as keyof typeof preferences] ? 'bg-white/20' : (isDarkMode ? 'bg-slate-700' : 'bg-white')}
                      `}>
                        {preferences[pref.key as keyof typeof preferences] ? (
                          <CheckCircle className="w-4 h-4 text-white" />
                        ) : (
                          <X className={`w-4 h-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                        )}
                      </div>
                      <pref.icon className={`w-4 h-4 ${preferences[pref.key as keyof typeof preferences] ? 'text-white' : (isDarkMode ? 'text-gray-400' : 'text-gray-600')}`} />
                      <span className={`text-sm flex-1 text-left ${preferences[pref.key as keyof typeof preferences] ? 'text-white' : (isDarkMode ? 'text-gray-300' : 'text-gray-700')}`}>
                        {pref.label}
                      </span>
                    </motion.button>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Select Wells */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className={`
                p-1 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl mb-6
              `}
            >
              <div className={`
                ${isDarkMode ? 'bg-gradient-to-br from-emerald-600 to-green-700' : 'bg-white/90'}
                backdrop-blur rounded-[0.9rem] p-5
              `}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-white/20' : 'bg-gradient-to-br from-emerald-500 to-green-600'}`}>
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <div className={`text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Select Wells
                    </div>
                  </div>
                  <span className={`px-3 py-1 text-xs rounded-full border ${isDarkMode ? 'bg-white/20 text-white border-white/30' : 'bg-emerald-500/20 text-emerald-600 border-emerald-500/30'}`}>
                    {selectedWells.length} selected
                  </span>
                </div>

                {/* Quick Select Actions */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <button
                    onClick={selectAllOverdue}
                    className={`px-2 py-2 rounded-lg text-xs font-medium ${isDarkMode ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-700'}`}
                  >
                    Overdue Only
                  </button>
                  <button
                    onClick={selectAll}
                    className={`px-2 py-2 rounded-lg text-xs font-medium ${isDarkMode ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-700'}`}
                  >
                    Select All
                  </button>
                  <button
                    onClick={clearSelection}
                    className={`px-2 py-2 rounded-lg text-xs font-medium ${isDarkMode ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-700'}`}
                  >
                    Clear
                  </button>
                </div>

                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {wells.map((well, index) => {
                    const isSelected = selectedWells.includes(well.id);
                    const status = getWellStatus(well);

                    return (
                      <motion.div
                        key={well.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 + index * 0.05 }}
                        onClick={() => handleWellToggle(well.id)}
                        className={`
                          p-3 rounded-xl cursor-pointer transition-all
                          ${isSelected
                            ? (isDarkMode ? 'bg-white/20 border border-white/30' : 'bg-emerald-500/10 border border-emerald-500/30')
                            : (isDarkMode ? 'bg-white/10' : 'bg-gray-100')
                          }
                        `}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`
                              w-4 h-4 rounded-full border-2 flex items-center justify-center
                              ${isSelected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-400'}
                            `}>
                              {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                            </div>
                            <div>
                              <div className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {well.name}
                              </div>
                              <div className={`text-xs ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>
                                {well.location}
                              </div>
                            </div>
                          </div>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${isDarkMode ? status.colorDark : status.colorLight}`}>
                            {status.label}
                          </span>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Generate Route Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={optimizeRoute}
                  disabled={optimizing || (startLocationCoords ? selectedWells.length < 1 : selectedWells.length < 2)}
                  className="w-full mt-4 py-4 bg-gradient-to-r from-emerald-600 to-green-700 rounded-xl text-white shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {optimizing ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      <span>Optimizing...</span>
                    </>
                  ) : (
                    <>
                      <Navigation className="w-5 h-5" />
                      <span>Generate Optimized Route</span>
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {activeSubTab === 'active' && (
          <motion.div
            key="active"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            {optimizedRoute ? (
              <>
                {/* Optimization Metrics - NEW */}
                {lastOptimizationMetrics && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-1 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl mb-4"
                  >
                    <div className={`
                      ${isDarkMode ? 'bg-gradient-to-br from-emerald-600 to-green-700' : 'bg-white/90'}
                      backdrop-blur rounded-[0.9rem] p-4
                    `}>
                      <div className="flex items-center gap-2 mb-3">
                        <Zap className={`w-4 h-4 ${isDarkMode ? 'text-white' : 'text-emerald-500'}`} />
                        <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          Optimization Metrics
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className={`text-xs mb-1 ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>
                            Algorithm
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full border ${isDarkMode ? 'bg-white/20 text-white border-white/30' : 'bg-emerald-500/20 text-emerald-600 border-emerald-500/30'}`}>
                            {lastOptimizationMetrics.algorithm.toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className={`text-xs mb-1 ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>
                            Improvement
                          </div>
                          <div className={`text-lg ${isDarkMode ? 'text-white font-bold' : 'text-emerald-600'}`}>
                            +{lastOptimizationMetrics.improvementPercent}%
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs mb-1 ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>
                            Time
                          </div>
                          <div className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {lastOptimizationMetrics.timeMs}ms
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs mb-1 ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>
                            Validation
                          </div>
                          {lastOptimizationMetrics.validationIssues === 0 ? (
                            <div className={`flex items-center gap-1 ${isDarkMode ? 'text-white' : 'text-emerald-600'}`}>
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-sm">Valid</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-red-500">
                              <AlertTriangle className="w-4 h-4" />
                              <span className="text-sm">{lastOptimizationMetrics.validationIssues} issues</span>
                            </div>
                          )}
                        </div>
                      </div>
                      {optimizedRoute.validation?.warnings && optimizedRoute.validation.warnings.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-700/50">
                          <div className="text-xs text-yellow-600 dark:text-yellow-400 flex items-start gap-2">
                            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            <span>{optimizedRoute.validation.warnings[0]}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* Route Progress */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="p-1 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl mb-6"
                >
                  <div className={`
                    ${isDarkMode ? 'bg-gradient-to-br from-cyan-600 to-blue-700' : 'bg-white/90'}
                    backdrop-blur rounded-[0.9rem] p-5
                  `}>
                    <div className="flex items-center justify-between mb-4">
                      <div className={`text-sm ${isDarkMode ? 'text-white/80' : 'text-gray-600'}`}>
                        Route Progress
                      </div>
                      <span className={`px-3 py-1 text-xs rounded-full border ${isDarkMode ? 'bg-white/20 text-white border-white/30' : 'bg-cyan-500/20 text-cyan-600 border-cyan-500/30'}`}>
                        {optimizedRoute.waypoints?.length || 0} wells
                      </span>
                    </div>

                    {/* Progress Bar */}
                    <div className={`h-3 rounded-full mb-4 ${isDarkMode ? 'bg-white/20' : 'bg-gray-200'}`}>
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${optimizedRoute && optimizedRoute.waypoints ? (visitedWells.length / optimizedRoute.waypoints.length) * 100 : 0}%` }}
                        transition={{ duration: 0.5, type: "spring" }}
                        className="h-full bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className={`text-xs mb-1 ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>ETA</div>
                        <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {(optimizedRoute.totalDuration / 60).toFixed(1)} hrs
                        </div>
                      </div>
                      <div>
                        <div className={`text-xs mb-1 ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>Distance</div>
                        <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {optimizedRoute.totalDistance.toFixed(1)} mi
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Current Well */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="p-1 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl mb-6"
                >
                  <div className={`
                    ${isDarkMode ? 'bg-gradient-to-br from-emerald-600 to-green-700' : 'bg-white/90'}
                    backdrop-blur rounded-[0.9rem] p-5
                  `}>
                    <div className="flex items-center gap-3 mb-3">
                      <motion.div
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl flex items-center justify-center shadow-xl"
                      >
                        <Radio className="w-6 h-6 text-white" />
                      </motion.div>
                      <div className="flex-1">
                        <div className={`text-xs mb-1 ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>Next Destination</div>
                        <div className={`text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {optimizedRoute.waypoints[0]?.name}
                        </div>
                      </div>
                      <ChevronRight className={`w-5 h-5 ${isDarkMode ? 'text-white/70' : 'text-gray-400'}`} />
                    </div>
                  </div>
                </motion.div>

                {/* Route Steps */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mb-6"
                >
                  <div className={`text-sm mb-3 ${isDarkMode ? 'text-white/80' : 'text-gray-600'}`}>
                    Route Order
                  </div>
                  <div className="space-y-2">
                    {optimizedRoute.waypoints.map((waypoint: WellLocation, index: number) => (
                      <motion.div
                        key={waypoint.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 + index * 0.1 }}
                        className={`
                          p-4 rounded-xl
                          ${index === 0
                            ? 'p-1 bg-gradient-to-br from-blue-500 to-cyan-600'
                            : (isDarkMode ? 'bg-white/10' : 'bg-gray-100')
                          }
                        `}
                      >
                        <div className={index === 0 ? `${isDarkMode ? 'bg-gradient-to-br from-blue-700 to-cyan-800' : 'bg-white/90'} backdrop-blur rounded-[0.7rem] p-4` : ''}>
                          <div className="flex items-start gap-3">
                            <div className={`
                              w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                              ${index === 0
                                ? (isDarkMode ? 'bg-white/20' : 'bg-gradient-to-br from-blue-500 to-cyan-600')
                                : (isDarkMode ? 'bg-white/10' : 'bg-white')
                              }
                            `}>
                              <span className={`text-sm ${index === 0 ? 'text-white' : (isDarkMode ? 'text-gray-300' : 'text-gray-700')}`}>
                                {index + 1}
                              </span>
                            </div>
                            <div className="flex-1">
                              <div className={`text-sm mb-1 ${index === 0 ? (isDarkMode ? 'text-white' : 'text-gray-900') : (isDarkMode ? 'text-gray-300' : 'text-gray-700')}`}>
                                {waypoint.name}
                              </div>
                              <div className={`text-xs ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>
                                {waypoint.latitude.toFixed(4)}, {waypoint.longitude.toFixed(4)}
                              </div>
                              {waypoint.priority && waypoint.priority !== 'medium' && (
                                <span className={`inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-medium ${isDarkMode
                                  ? (waypoint.priority === 'emergency' ? 'bg-red-600 text-white' :
                                    waypoint.priority === 'high' ? 'bg-orange-500 text-white' :
                                      'bg-emerald-600 text-white')
                                  : (waypoint.priority === 'emergency' ? 'bg-red-100 text-red-800' :
                                    waypoint.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                                      'bg-green-100 text-green-800')
                                  }`}>
                                  {waypoint.priority}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                {/* Action Buttons */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={startNavigation}
                    className="py-4 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl text-white shadow-lg flex items-center justify-center gap-2"
                  >
                    <Navigation className="w-5 h-5" />
                    <span className="text-sm">Navigate</span>
                  </motion.button>

                  {/* Feature 5: Share Route Button */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowShareDialog(true)}
                    className={`
                      py-4 rounded-xl flex items-center justify-center gap-2
                      bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg
                    `}
                  >
                    <Share2 className="w-5 h-5" />
                    <span className="text-sm">Share</span>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={exportRoute}
                    className={`
                      py-4 rounded-xl flex items-center justify-center gap-2 border
                      ${isDarkMode ? 'border-white/30 text-white bg-white/10 hover:bg-white/20' : 'border-gray-200 text-gray-900 hover:bg-gray-50'}
                    `}
                  >
                    <Download className="w-5 h-5" />
                    <span className="text-sm">Export</span>
                  </motion.button>
                </div>
              </>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`
                  text-center py-20
                  ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}
                `}
              >
                <RouteIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>No active route</p>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveSubTab('planner')}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm mt-4"
                >
                  Create Route
                </motion.button>
              </motion.div>
            )}
          </motion.div>
        )}

        {activeSubTab === 'history' && (
          <motion.div
            key="history"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-3"
          >
            {routes.length > 0 ? (
              routes.map((route, index) => (
                <motion.div
                  key={route.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => loadHistoryRoute(route)}
                  className={`p-1 bg-gradient-to-br from-slate-500 to-gray-600 rounded-2xl cursor-pointer hover:scale-[1.02] transition-transform`}
                >
                  <div className={`
                    ${isDarkMode ? 'bg-gradient-to-br from-slate-700 to-gray-800' : 'bg-white/90'}
                    backdrop-blur rounded-[0.9rem] p-5
                  `}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`
                          w-10 h-10 rounded-xl flex items-center justify-center
                          ${isDarkMode ? 'bg-white/20' : 'bg-gradient-to-br from-slate-500 to-gray-600'}
                        `}>
                          <RouteIcon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className={`text-base mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {route.name}
                          </div>
                          <div className={`text-xs mb-2 ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>
                            {route.wells?.length || 0} wells • {route.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown date'}
                          </div>
                          <div className="flex gap-2">
                            <span className={`
                              px-2 py-1 rounded-lg text-xs
                              ${isDarkMode ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-700'}
                            `}>
                              {route.status}
                            </span>
                            {route.totalDistance && (
                              <span className={`
                                px-2 py-1 rounded-lg text-xs
                                ${isDarkMode ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-700'}
                              `}>
                                {route.totalDistance.toFixed(1)} mi
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <ChevronRight className={`w-5 h-5 ${isDarkMode ? 'text-white/70' : 'text-gray-400'}`} />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className={`text-center py-10 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                <HistoryIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Route history coming soon</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Genetic Solver Overlay */}
      <AnimatePresence>
        {showSolverAnimation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#0f0f13] flex items-center justify-center p-4"
          >
            <div className="w-full h-full max-w-5xl max-h-[90vh]">
              <SmartRouteOptimizer
                autoStart={true}
                onComplete={onSolverComplete}
                className="h-full border-none shadow-none"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 🌐 Industry Well Explorer - Full Screen OCC Navigation */}
      <AnimatePresence>
        {showIndustryExplorer && (
          <IndustryWellExplorer
            onClose={() => setShowIndustryExplorer(false)}
            onNavigateToWell={(occWell: OCCCompanyWell) => {
              // Close the explorer
              setShowIndustryExplorer(false);

              // Create a navigation route for this OCC well
              const navRoute = {
                id: `occ-${occWell.id}`,
                name: `Navigate to ${occWell.name}`,
                wells: [{
                  wellId: occWell.id,
                  wellName: occWell.name,
                  order: 1,
                  coordinates: { lat: occWell.lat, lng: occWell.lng },
                  estimatedTime: 0.5,
                  distance: occWell.distance || 0,
                  status: occWell.formattedStatus,
                  operator: occWell.operator,
                  api: occWell.api
                }],
                totalDistance: occWell.distance || 0,
                estimatedTime: 0.5,
                startCoordinates: startLocationCoords || undefined
              };

              // Pass to parent's navigation handler
              onStartNavigation(navRoute);
              toast.success(`Starting navigation to ${occWell.name}`);
            }}
            onAddToRoute={(occWell: OCCCompanyWell) => {
              // Add to selected wells for route planning
              // (Future: could integrate with route builder)
              toast.success(`${occWell.name} added to route planner`);
            }}
            companyWells={wells}
            isDarkMode={isDarkMode}
          />
        )}

        {/* Feature 5: Share Route Dialog */}
        {showShareDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setShowShareDialog(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className={`
                w-full max-w-md rounded-2xl shadow-2xl overflow-hidden
                ${isDarkMode ? 'bg-slate-900' : 'bg-white'}
              `}
            >
              {/* Dialog Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
                    <Share2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Share Route
                    </h3>
                    <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      Select team members to share with
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowShareDialog(false)}
                  className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-gray-100'}`}
                >
                  <X className={`w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`} />
                </button>
              </div>

              {/* Team Members List */}
              <div className="max-h-[300px] overflow-y-auto p-4 space-y-2">
                {teamMembers.length === 0 ? (
                  <div className={`text-center py-8 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No team members found</p>
                  </div>
                ) : (
                  teamMembers.map((member) => (
                    <button
                      key={member.id}
                      onClick={() => {
                        setSelectedShareTargets(prev =>
                          prev.includes(member.id)
                            ? prev.filter(id => id !== member.id)
                            : [...prev, member.id]
                        );
                      }}
                      className={`
                        w-full flex items-center gap-3 p-3 rounded-xl transition-all
                        ${selectedShareTargets.includes(member.id)
                          ? 'bg-purple-600 text-white'
                          : isDarkMode
                            ? 'bg-slate-800 text-white hover:bg-slate-700'
                            : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                        }
                      `}
                    >
                      <div className={`
                        w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold
                        ${selectedShareTargets.includes(member.id)
                          ? 'bg-white/20 text-white'
                          : 'bg-purple-600/20 text-purple-600'
                        }
                      `}>
                        {member.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="flex-1 text-left font-medium">{member.name}</span>
                      {selectedShareTargets.includes(member.id) && (
                        <CheckCircle className="w-5 h-5" />
                      )}
                    </button>
                  ))
                )}
              </div>

              {/* Dialog Footer */}
              <div className="px-5 py-4 border-t border-gray-200 dark:border-slate-700">
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={handleShareRoute}
                  disabled={isSharing || selectedShareTargets.length === 0}
                  className={`
                    w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2
                    ${selectedShareTargets.length > 0
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }
                  `}
                >
                  {isSharing ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Share2 className="w-5 h-5" />
                      <span>Share with {selectedShareTargets.length} member{selectedShareTargets.length !== 1 ? 's' : ''}</span>
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Feature 5: Shared Routes Notification Badge */}
        {sharedRoutes.length > 0 && activeSubTab !== 'active' && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="fixed bottom-20 left-4 z-50"
          >
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                if (sharedRoutes[0]) {
                  handleAcceptSharedRoute(sharedRoutes[0]);
                }
              }}
              className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl shadow-lg text-white"
            >
              <Share2 className="w-5 h-5" />
              <div className="text-left">
                <p className="text-sm font-semibold">{sharedRoutes.length} route(s) shared</p>
                <p className="text-xs opacity-80">Tap to load</p>
              </div>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </div >
  );
}
